/**
 * A strong password, generated in the browser, for an administrator to hand over.
 *
 * **`crypto.getRandomValues`, never `Math.random`.** `Math.random` is not a cryptographic source: its
 * output is predictable from earlier output, and a credential drawn from it is guessable by anyone who can
 * see one other password the same tab produced.
 *
 * **Letters and digits only, and length carries the entropy.** The same choice as the deployment's secret
 * generator, for a related reason: this value is read off a screen, typed into a chat window and typed
 * again by whoever receives it, and a punctuation character that renders differently in two fonts turns a
 * password into a support call. 24 characters from this alphabet is about 143 bits.
 *
 * The modulo is unbiased because 62 divides the 8-bit range unevenly: bytes at or above the largest
 * multiple of 62 are discarded rather than folded, which would make the first two letters of the alphabet
 * slightly likelier than the rest.
 */
const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
const LENGTH = 24;
const LIMIT = Math.floor(256 / ALPHABET.length) * ALPHABET.length;

export const generatePassword = (): string => {
  let password = '';

  while (password.length < LENGTH) {
    const bytes = new Uint8Array(LENGTH);

    crypto.getRandomValues(bytes);

    for (const byte of bytes) {
      if (byte < LIMIT && password.length < LENGTH) {
        password += ALPHABET[byte % ALPHABET.length];
      }
    }
  }

  return password;
};
